/**
 * Início — a primeira tela, pensada para o vendedor na rua.
 *
 * Responde, nesta ordem, às perguntas de quem abre o app entre uma visita e
 * outra: quanto falta para a meta, como estou indo hoje/semana/mês, o que
 * precisa de atenção e onde estou no ranking. Sem filtro e sem gráfico de
 * análise — quem quer recortar e comparar abre Vendas.
 *
 * Tudo vem de uma chamada só (`/inicio`), para abrir rápido no 4G.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarCheck,
  CalendarDays,
  CalendarRange,
  Info,
  Target,
  Trophy,
  TrendingDown,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { competencia, moedaCompacta, nomeProprio, numero, primeiroNome } from '../lib/format.ts';
import { cx } from '../lib/cx.ts';
import { Aviso, Card, Esqueleto } from '../components/ui/index.tsx';
import { CartaoDado, GradeBento, type Variacao } from '../components/ui/Bento.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';

interface Comparacao {
  atual: number;
  anterior: number;
}

interface ItemRanking {
  posicao: number;
  codigo: string;
  nome: string;
  placas: number;
}

interface RespostaInicio {
  hoje: string;
  escopo: 'consultor' | 'geral';
  semVinculo: boolean;
  adesoes: { hoje: Comparacao; semana: Comparacao; mes: Comparacao };
  cancelamentos: Comparacao;
  meta: { placas: number; realizado: number; diasRestantes: number } | null;
  ranking: { meuCodigo: string | null; itens: ItemRanking[] };
  inadimplentes: { placas: number; valor: number } | null;
}

/** Quantos do topo aparecem antes de "ver todos". */
const TOPO = 10;

export default function Inicio() {
  const { usuario, pode } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['inicio'],
    queryFn: () => api.get<RespostaInicio>('/inicio'),
    refetchInterval: 60_000,
  });

  const dataExtenso = data
    ? new Date(`${data.hoje}T12:00:00`).toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    : '';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-[22px] font-semibold text-[var(--fg)]">
          Olá, {usuario ? primeiroNome(nomeProprio(usuario.nome)) : ''}
        </h2>
        <p className="text-[13px] first-letter:uppercase text-[var(--muted)]">
          {dataExtenso}
          {data && (data.escopo === 'consultor' ? ' · suas vendas' : ' · toda a associação')}
        </p>
      </div>

      {isLoading || !data ? (
        <Carregando />
      ) : (
        <>
          {data.semVinculo && (
            <Aviso tom="alerta" icone={<Info size={14} />}>
              Seu usuário ainda não está vinculado a um código de voluntário do SGA, por isso os
              números aparecem zerados. Peça à diretoria para fazer o vínculo.
            </Aviso>
          )}

          {/* Bento: a meta é o herói (2×2 no desktop); os números do período
              e o que pede atenção encaixam ao redor. Sem meta, a grade fecha
              em duas linhas cheias: Hoje, Semana e o mês; depois inadimplência
              e cancelamentos. No celular, Hoje e Semana vão em par e o resto
              em linhas cheias. */}
          <GradeBento>
            <CartaoMeta meta={data.meta} mes={data.hoje.slice(0, 7)} />
            <CartaoDado
              Icone={CalendarCheck}
              tom="marca"
              rotulo="Hoje"
              valor={numero(data.adesoes.hoje.atual)}
              variacao={comparar(data.adesoes.hoje, 'vs ontem')}
            />
            <CartaoDado
              Icone={CalendarRange}
              tom="marca"
              rotulo="Semana"
              valor={numero(data.adesoes.semana.atual)}
              variacao={comparar(data.adesoes.semana, 'vs semana anterior')}
            />
            <CartaoDado
              tamanho="largo"
              Icone={CalendarDays}
              tom="marca"
              rotulo="Adesões no mês"
              valor={numero(data.adesoes.mes.atual)}
              variacao={comparar(data.adesoes.mes, 'vs mesmo ponto do mês passado')}
              nota={
                data.meta
                  ? undefined
                  : `Nenhuma meta definida para ${competencia(data.hoje.slice(0, 7)).split(' ')[0]}.`
              }
            />
            {data.inadimplentes && (
              <CartaoDado
                tamanho="largo"
                para={
                  pode('admin', 'diretor', 'gerente_regional', 'financeiro', 'consultor')
                    ? '/financeiro/inadimplentes'
                    : undefined
                }
                Icone={AlertTriangle}
                tom={data.inadimplentes.placas > 0 ? 'perigo' : 'neutro'}
                rotulo={data.escopo === 'consultor' ? 'Seus inadimplentes' : 'Inadimplentes'}
                valor={numero(data.inadimplentes.placas)}
                nota={`${moedaCompacta(data.inadimplentes.valor)} em atraso`}
              />
            )}
            <CartaoDado
              // Sem o cartão de inadimplência (perfil rastreamento), ocupa a
              // linha inteira em vez de deixar meia linha vazia.
              tamanho={data.inadimplentes ? 'largo' : 'total'}
              Icone={TrendingDown}
              tom={data.cancelamentos.atual > data.cancelamentos.anterior ? 'perigo' : 'neutro'}
              rotulo="Cancelamentos no mês"
              valor={numero(data.cancelamentos.atual)}
              variacao={comparar(data.cancelamentos, 'vs mesmo ponto do mês passado', true)}
            />
          </GradeBento>

          <Ranking itens={data.ranking.itens} meuCodigo={data.ranking.meuCodigo} />
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Meta ───────────────────────────

function CartaoMeta({
  meta,
  mes,
}: {
  meta: RespostaInicio['meta'];
  mes: string;
}) {
  const nomeMes = competencia(mes).split(' ')[0];

  if (!meta) {
    // Sem meta, o cartão repetiria o número de "Adesões no mês" ao lado — e
    // sem barra nem ritmo, deixava meio 2×2 vazio. O aviso vai para lá.
    return null;
  }

  const pct = meta.placas > 0 ? (meta.realizado / meta.placas) * 100 : 0;
  const faltam = Math.max(meta.placas - meta.realizado, 0);
  const batida = faltam === 0;
  // Ritmo para fechar: arredondado para cima, porque meia placa não se vende.
  const porDia = Math.ceil(faltam / Math.max(meta.diasRestantes, 1));

  return (
    <CartaoDado
      tamanho="destaque"
      tom={batida ? 'sucesso' : 'marca'}
      Icone={Target}
      rotulo={`Meta de ${nomeMes}`}
      valor={numero(meta.realizado)}
      variacao={{ texto: `${Math.round(pct)}%`, detalhe: `de ${numero(meta.placas)} placas`, bom: batida ? true : null }}
    >
      <div
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progresso da meta"
        className="h-3 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-200)]"
      >
        <div
          className="h-full rounded-[var(--r-full)] transition-[width] duration-700"
          style={{
            width: `${Math.min(pct, 100)}%`,
            background: batida ? 'var(--success)' : 'var(--brand)',
          }}
        />
      </div>
      <p className="mt-3 text-[15px] leading-snug text-[var(--fg)]">
        {batida ? (
          <>
            Meta batida
            {meta.realizado > meta.placas && ` — ${numero(meta.realizado - meta.placas)} acima`}.
          </>
        ) : (
          <>
            Faltam <strong className="num">{numero(faltam)}</strong>{' '}
            {faltam === 1 ? 'placa' : 'placas'} em {meta.diasRestantes}{' '}
            {meta.diasRestantes === 1 ? 'dia' : 'dias'}
            <span className="block text-[13px] text-[var(--muted)]">
              cerca de {numero(porDia)} por dia para fechar
            </span>
          </>
        )}
      </p>
    </CartaoDado>
  );
}

/** Pílula de variação: atual × anterior, em números absolutos. */
function comparar(c: Comparacao, detalhe: string, subirEhRuim = false): Variacao {
  const dif = c.atual - c.anterior;
  if (dif === 0) return { texto: '= igual', detalhe, bom: null };
  return {
    texto: `${dif > 0 ? '▲' : '▼'} ${numero(Math.abs(dif))}`,
    detalhe,
    bom: subirEhRuim ? dif < 0 : dif > 0,
  };
}

// ─────────────────────────── Ranking ───────────────────────────

function Ranking({ itens, meuCodigo }: { itens: ItemRanking[]; meuCodigo: string | null }) {
  const [todos, setTodos] = useState(false);
  const eu = meuCodigo ? itens.find((i) => i.codigo === meuCodigo) : undefined;
  const maior = itens[0]?.placas ?? 0;

  // Fora do topo, a própria linha aparece mesmo assim — é a que a pessoa procura.
  const visiveis = todos ? itens : itens.slice(0, TOPO);
  const euFora = eu && !visiveis.includes(eu);

  // Quantas placas separam de quem está logo acima.
  const acima = eu ? [...itens].reverse().find((i) => i.placas > eu.placas) : undefined;

  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <Trophy size={16} className="text-[var(--brand)]" />
        <h3 className="text-[15px] font-medium text-[var(--fg)]">Ranking do mês</h3>
        <span className="ml-auto text-xs text-[var(--faint)]">adesões</span>
      </div>

      {eu && (
        <p className="mb-3 rounded-[var(--r-md)] bg-[var(--brand-subtle)] px-3 py-2 text-[15px] text-[var(--fg)]">
          Você está em <strong>{eu.posicao}º</strong> de {itens.length}
          {acima && (
            <span className="text-[var(--muted)]">
              {' '}
              · {numero(acima.placas - eu.placas)}{' '}
              {acima.placas - eu.placas === 1 ? 'placa' : 'placas'} para subir
            </span>
          )}
        </p>
      )}

      {itens.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-[var(--muted)]">
          Nenhuma adesão registrada neste mês ainda.
        </p>
      ) : (
        <ol className="flex list-none flex-col p-0">
          {visiveis.map((i) => (
            <LinhaRanking key={i.codigo} item={i} maior={maior} minha={i.codigo === meuCodigo} />
          ))}
          {euFora && (
            <>
              <li aria-hidden className="py-1 text-center text-[var(--faint)]">
                ⋯
              </li>
              <LinhaRanking item={eu} maior={maior} minha />
            </>
          )}
        </ol>
      )}

      {itens.length > TOPO && (
        <button
          onClick={() => setTodos((v) => !v)}
          className="anel-foco mt-2 h-11 w-full rounded-[var(--r-md)] text-[13px] font-medium text-[var(--muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--fg)]"
        >
          {todos ? 'Mostrar só os 10 primeiros' : `Ver todos os ${itens.length}`}
        </button>
      )}
    </Card>
  );
}

function LinhaRanking({
  item,
  maior,
  minha,
}: {
  item: ItemRanking;
  maior: number;
  minha: boolean;
}) {
  return (
    <li
      className={cx(
        'flex min-h-11 items-center gap-3 rounded-[var(--r-md)] px-2 py-1.5',
        minha && 'bg-[var(--brand-subtle)]',
      )}
    >
      <span
        className={cx(
          'num w-6 shrink-0 text-right text-[13px]',
          item.posicao <= 3 ? 'font-semibold text-[var(--fg)]' : 'text-[var(--faint)]',
        )}
      >
        {item.posicao}º
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cx(
            'truncate text-[14px]',
            minha ? 'font-semibold text-[var(--fg)]' : 'text-[var(--fg)]',
          )}
        >
          {nomeProprio(item.nome)}
          {minha && <span className="font-normal text-[var(--muted)]"> (você)</span>}
        </p>
        <div className="mt-1 h-1 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-200)]">
          <div
            className="h-full rounded-[var(--r-full)]"
            style={{
              width: `${maior > 0 ? (item.placas / maior) * 100 : 0}%`,
              background: minha ? 'var(--brand)' : 'var(--gray-500)',
            }}
          />
        </div>
      </div>
      <span className="num w-8 shrink-0 text-right text-[15px] font-semibold text-[var(--fg)]">
        {numero(item.placas)}
      </span>
    </li>
  );
}

function Carregando() {
  // Mesmo desenho da grade carregada, para nada pular quando os dados chegam.
  return (
    <div className="flex flex-col gap-4">
      <GradeBento>
        <Esqueleto className="col-span-2 h-[200px] rounded-[20px] lg:row-span-2 lg:h-auto" />
        <Esqueleto className="h-[120px] rounded-[20px]" />
        <Esqueleto className="h-[120px] rounded-[20px]" />
        <Esqueleto className="col-span-2 h-[120px] rounded-[20px]" />
      </GradeBento>
      <Esqueleto className="h-[320px] rounded-[20px]" />
    </div>
  );
}
